// Central source of truth for the current season year.
// Update this once per year when the new season starts.
export const SEASON_YEAR = 2026;

// 13 lineup weeks: 1-10 regular season, 11-13 playoffs. Fixed for every league
// size — the bracket format varies by manager count, the calendar does not.
export const REGULAR_SEASON_WEEKS = 10;
export const FIRST_PLAYOFF_WEEK = 11;
export const TOTAL_LINEUP_WEEKS = 13;

// Free agency closes for the final regular week and all playoff weeks, so
// week 9 is the last week a roster can change.
// The week whose games open the first bidding window. That window runs from
// 10am Sunday, when the week advances to 4, to 4am Tuesday, and resolves into
// week 4 (see getBiddingWindow below). Budgets derive from reverse standings,
// which say little after one or two slates. Duplicated in
// backend/waiverWindow.js and in migrations 044 and 071.
export const FIRST_FREE_AGENCY_WEEK = 3;

// Super Dog. An underdog of this many points or more keeps the -3 loss
// penalty cancelled, so starting a heavy underdog is a real choice rather
// than an automatic three-point hole. Duplicated as SUPER_DOG_SPREAD in
// backend/scoring.js, which is the scorer of record.
export const SUPER_DOG_SPREAD = 14.5;

// Free agency closes when the season actually starts playing, not when the
// week label flips. The week 0 label turns over at the Tuesday boundary like
// every other week, but no football has been played by then, so closing the
// board on Tuesday would take two days of roster work away for nothing.
//
// This is the first kickoff of week 0, in Eastern. Hardcoded for the same
// reason SEASON_WEEKS is: deriving it from `games` would let a TBD kickoff
// time move the deadline under managers who had already planned around it.
export const SEASON_OPENS_AT = "2026-08-29T12:00:00-04:00";

// When the fantasy game starts, which is not when college football starts.
// Week 0 plays a full weekend and awards nothing: the first kickoff that
// scores is the first game of lineup week 1. Managers need both dates and
// they are five days apart, so conflating them would have someone believe
// their lineup mattered on the opening Saturday.
//
// Hardcoded for the same reason as SEASON_WEEKS and SEASON_OPENS_AT: deriving
// it from `games` lets a rescheduled kickoff move a date managers have
// already planned around.
export const SCORING_OPENS_AT = "2026-09-03T18:00:00-04:00";

// How long after its draft a late-drafting league keeps the instant add
// board. A league that drafts in week 1 or later has no preseason window and
// no waivers until FIRST_FREE_AGENCY_WEEK, so without this it would be stuck
// with its drafted roster for weeks.
export const POST_DRAFT_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the instant add board is open for one league.
 *
 * Global until the season opens, then per league: a league that drafted late
 * gets POST_DRAFT_GRACE_MS from the moment its draft finished.
 *
 * This is the board as a whole. Inside it each team still closes on its own,
 * at its lineup lock (isTeamLocked in gameStatus.js, team_is_locked in the
 * database): the week's first kickoff no longer shuts every team at once
 * (migration 076). A Thursday night game used to close the board for teams
 * that would not play until Saturday.
 *
 * `faLocked` is the manual override and beats everything, which is what makes
 * it a usable kill switch mid-season.
 */
export function isInstantAddOpen({
  seasonConfig,
  draftCompletedAt,
  isTestLeague = false,
  now = new Date(),
} = {}) {
  if (seasonConfig?.faLocked) return false;

  // The weekly window after bids resolve.
  if (isWeeklyFreeAgencyOpen({ seasonConfig, isTestLeague, now })) return true;

  if (now.getTime() < new Date(SEASON_OPENS_AT).getTime()) return true;

  if (!draftCompletedAt) return false;
  const completed = new Date(draftCompletedAt).getTime();
  if (Number.isNaN(completed)) return false;

  return now.getTime() < completed + POST_DRAFT_GRACE_MS;
}
export const LAST_FREE_AGENCY_WEEK = 9;

// The week whose games open the last bidding window.
//
// One week earlier than LAST_FREE_AGENCY_WEEK. The window after week 8 opens
// when the week advances to 9 and resolves into week 9 on the Tuesday. A window
// after week 9 would resolve into week 10, which is closed.
export const LAST_BID_WEEK = LAST_FREE_AGENCY_WEEK - 1;

// Waiver budgets, from waiver_budget_rows (migration 037): ranked by season
// points, last place gets WAIVER_BUDGET_MAX and each place higher gets
// WAIVER_BUDGET_STEP less, never below WAIVER_BUDGET_MIN. Recomputed for every
// window, so nothing carries over and a win costs nothing later. The floor
// only matters in a league of 21 or more.
export const WAIVER_BUDGET_MAX = 100;
export const WAIVER_BUDGET_STEP = 5;
export const WAIVER_BUDGET_MIN = 5;

// The most pending claims a manager can hold in a league at once (migration
// 073's place_bid). Ranked claims (migration 072) let a manager stack
// backups on the same budget, so this is what keeps that from turning into
// an unbounded list. Duplicated in place_bid as a plpgsql constant, the same
// pattern as the week numbers above; the backend cannot import this file.
export const WAIVER_MAX_CLAIMS = 5;

/**
 * The budget for a standings place in a league of `leagueSize`, 1 being first.
 */
export function waiverBudgetForPlace(place, leagueSize) {
  const fromBottom = leagueSize - place; // 0 for last place
  return Math.max(WAIVER_BUDGET_MAX - WAIVER_BUDGET_STEP * fromBottom, WAIVER_BUDGET_MIN);
}

// Bidding is a fixed 42 hour window each week, not the whole week: 10am Sunday
// to 4am Tuesday, Eastern. The week advances at 10am Sunday (backend/season.js),
// so the window runs in the new week, once every Saturday game of the one
// before is over, and bids resolve into that new week at 6am Tuesday. Closing
// at 4am leaves the resolve run two clear hours, so no bid lands while it is
// running and sits pending for a week.
//
// It is a clock rule rather than "every game is final" because a Saturday game
// ingestion never closed out would otherwise hold the window shut for every
// league. A game still hanging on Sunday keeps being scored into its own week.
//
// Enforced by place_bid (migration 071), which carries its own copy of these
// hours. This copy only decides what the app shows.
export const BID_WINDOW_OPENS_HOUR = 10;  // Sunday, Eastern
export const BID_WINDOW_CLOSES_HOUR = 4;  // Tuesday, Eastern
export const WAIVER_RESOLVE_HOUR = 6;     // Tuesday, Eastern

const EASTERN_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  weekday: "short",
  hour: "numeric",
  hourCycle: "h23",
});
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Day of week (0 = Sunday) and hour in Eastern time. */
export function easternDayHour(now = new Date()) {
  const parts = Object.fromEntries(EASTERN_PARTS.formatToParts(now).map((p) => [p.type, p.value]));
  return { day: WEEKDAYS[parts.weekday], hour: parseInt(parts.hour, 10) % 24 };
}

const EASTERN_MINUTE = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", minute: "numeric" });

/**
 * The most recent 6am Tuesday Eastern at or before `now`: when resolve_waivers
 * last ran, or would have. For deciding whether a cached copy predates it.
 *
 * Counts back in whole days and hours from Eastern wall time, so across the
 * November clock change it can be an hour off. A cache check can afford that.
 */
export function lastWaiverRunAt(now = new Date()) {
  const { day, hour } = easternDayHour(now);
  const minute = parseInt(EASTERN_MINUTE.format(now), 10) || 0;
  const HOUR = 60 * 60 * 1000;
  let sinceRun = ((day - 2 + 7) % 7) * 24 * HOUR + (hour - WAIVER_RESOLVE_HOUR) * HOUR + minute * 60 * 1000;
  if (sinceRun < 0) sinceRun += 7 * 24 * HOUR;
  const seconds = now.getSeconds() * 1000 + now.getMilliseconds();
  return new Date(now.getTime() - sinceRun - seconds);
}

/** Whether `now` falls inside the Sunday 10am to Tuesday 4am Eastern window. */
export function isBiddingHours(now = new Date()) {
  const { day, hour } = easternDayHour(now);
  return (day === 0 && hour >= BID_WINDOW_OPENS_HOUR)
    || day === 1
    || (day === 2 && hour < BID_WINDOW_CLOSES_HOUR);
}

/**
 * Where bidding stands right now.
 *
 * `state` is one of: 'locked' (faLocked), 'before' (the first window has not
 * come round yet), 'waiting' (between windows), 'open', 'resolving' (Tuesday
 * between the close and the 6am run), or 'closed' (after the last window).
 *
 * `week` is the parsed config week, or NaN. `afterWeek` is the week whose games
 * the current or next window follows: inside the window that is the week just
 * finished, outside it the week being played.
 *
 * `isTestLeague` (leagues.is_test) ignores the clock: the window is open at any
 * time and follows the week being played, matching place_bid (migrations 068
 * and 069), so the bid flow can be tried without opening bidding for real
 * leagues. Unless config.season.testWindow is 'free_agency', which switches
 * every test league over to the weekly free agency window instead.
 */
export function getBiddingWindow({ seasonConfig, now = new Date(), isTestLeague = false } = {}) {
  const week = parseInt(String(seasonConfig?.currentWeek ?? '').replace(/\D/g, ''), 10);
  if (seasonConfig?.faLocked) return { state: 'locked', week, afterWeek: NaN };
  if (!Number.isFinite(week)) return { state: 'before', week, afterWeek: FIRST_FREE_AGENCY_WEEK };

  if (isTestLeague) {
    if (testWindowOf(seasonConfig) === 'free_agency') return { state: 'waiting', week, afterWeek: week };
    if (week < FIRST_FREE_AGENCY_WEEK) return { state: 'before', week, afterWeek: week };
    if (week > LAST_BID_WEEK) return { state: 'closed', week, afterWeek: week };
    return { state: 'open', week, afterWeek: week };
  }

  const inHours = isBiddingHours(now);
  const { day, hour } = easternDayHour(now);
  const resolving = day === 2 && hour >= BID_WINDOW_CLOSES_HOUR && hour < WAIVER_RESOLVE_HOUR;
  const afterWeek = inHours || resolving ? week - 1 : week;

  if (afterWeek < FIRST_FREE_AGENCY_WEEK) return { state: 'before', week, afterWeek };
  if (afterWeek > LAST_BID_WEEK) return { state: 'closed', week, afterWeek };
  if (inHours) return { state: 'open', week, afterWeek };
  if (resolving) return { state: 'resolving', week, afterWeek };
  return { state: 'waiting', week, afterWeek };
}

// Free agency after the bids. Once the 6am Tuesday run has resolved a
// window's bids, any unowned team can be added instantly, first come first
// served, until the next bidding window opens at 10am Sunday. So it follows
// the same weeks as bidding: the config week is 4 to 9. The two windows meet
// with no gap between them.
//
// Inside it, each team closes on its own at its lineup lock, and a team whose
// game has started cannot be dropped either (migration 076). This function is
// the window only; isTeamLocked in gameStatus.js is the per-team half.
//
// It opens at WAIVER_RESOLVE_HOUR on the clock, but the gate that holds is
// instant_add_team (migration 069), which also refuses while the league has
// any pending bid. A run that is late or failed therefore cannot let an
// instant add take a team a sealed bid was about to win. The app cannot see
// other managers' bids, so it shows the window by the clock alone.
//
// A test league has no clock here: it is open whenever
// config.season.testWindow is 'free_agency'.
const testWindowOf = (seasonConfig) =>
  seasonConfig?.testWindow === 'free_agency' ? 'free_agency' : 'bidding';

export function isWeeklyFreeAgencyOpen({
  seasonConfig,
  isTestLeague = false,
  now = new Date(),
} = {}) {
  if (seasonConfig?.faLocked) return false;
  const week = parseInt(String(seasonConfig?.currentWeek ?? '').replace(/\D/g, ''), 10);
  if (!Number.isFinite(week)) return false;

  if (isTestLeague) {
    return testWindowOf(seasonConfig) === 'free_agency'
      && week >= FIRST_FREE_AGENCY_WEEK && week <= LAST_FREE_AGENCY_WEEK;
  }

  const afterWeek = week - 1;
  if (afterWeek < FIRST_FREE_AGENCY_WEEK || afterWeek > LAST_BID_WEEK) return false;

  // Sunday from 10am to Tuesday 4am is the bidding window, and Tuesday before
  // 6am is waiting on the run.
  if (isBiddingHours(now)) return false;
  const { day, hour } = easternDayHour(now);
  if (day === 2 && hour < WAIVER_RESOLVE_HOUR) return false;

  return true;
}

// Week 0, the late-August opening weekend, is excluded from ONE thing only:
// fantasy scoring. backend/scoring.js returns early for it, because only 8
// teams play it and letting them bank points while ~120 sit idle would hand
// out an advantage set by the schedule rather than by management.
//
// Everything else treats it as the real football it is. It is ingested, its
// spreads are written, and it counts toward W-L and ATS records:
// updateTeamRecords has no week filter, deliberately. It is also displayed,
// always labelled "Wk 0" so it can never be mistaken for a scoring week.
//
// Do not "tidy" this by filtering week 0 out of records or ingestion. ATS
// grading needs cfbd.js to keep writing home_spread for those games, and
// hiding a game whose result already moved the record shown beside it would
// leave the record unexplainable.
//
// The split in practice:
//   "Next game" (Teams tab, trending cards) means the next SCORING game, so
//   it starts at FIRST_SCORING_WEEK and never shows a week 0 fixture. A
//   manager sizing up a team to draft cares what it can earn them, and an
//   August 29 game earns nothing.
//
//   A team's own schedule (TeamDrawer's strip) is the full fixture list and
//   does show week 0, labelled, because it is part of that team's season.
export const WEEK_ZERO = 0;
export const FIRST_SCORING_WEEK = 1;

/** Whether a week awards fantasy points. Week 0 is the only one that doesn't. */
export const isScoringWeek = (week) => Number(week) !== WEEK_ZERO;

// Six teams make the playoffs, whatever the league size (8, 10 or 12).
//
// Not a preference, a consequence of the calendar: the playoffs are three
// weeks, and six is the only field that fills three weeks cleanly. The top two
// seeds take a bye, 3v6 and 4v5 play the opening round, and the winners meet
// them in the semi-finals.
//
//   Week 11  3v6, 4v5              seeds 1 and 2 bye
//   Week 12  1 vs W(3/6), 2 vs W(4/5)
//   Week 13  Final
//
// A four-team bracket only fills two weeks, and stretching it to three needs a
// play-in for seeds 3 to 6, which is this bracket again. The cost is that in an
// 8-team league only two managers miss out, so the consolation pool there is a
// two-horse race.
export const PLAYOFF_TEAMS = 6;

// Seeds 1 and 2 skip the opening round (the bracket above).
export const PLAYOFF_BYES = 2;

// Everyone below the playoff line plays a single pool over the same three
// weeks: no bracket, no seeding, highest total across the playoff weeks takes
// next season's number 1 pick.
export const CONSOLATION_PRIZE = "first pick in next season's draft";

// The captain is mandatory from lineup week 1. Every manager has one in every
// scoring week, and backend/lineups.js assigns one to anybody who has not
// picked. It used to open in week 2; that restriction is gone.
export const FIRST_CAPTAIN_WEEK = 1;

// The 5x Play opens in lineup week 2 and closes at the end of the regular
// season. It sits on the captain, so it stacks the captain's 2x with its own
// 3x, which is why it is named for the 5x the team actually scores.
// backend/tripplay.js applies any unused one in LAST_TRIP_PLAY_WEEK.
//
// Keep these in step with the copies in backend/tripplay.js. The backend cannot
// import this file, and a cron job that disagrees with the rules page about
// which week it is spends a manager's 5x Play in a week they cannot use it.
export const FIRST_TRIP_PLAY_WEEK = 2;
export const LAST_TRIP_PLAY_WEEK = REGULAR_SEASON_WEEKS;

// The season calendar.
//
// `cfbWeek` is the college football week, which is what `games.week` stores.
// `lineupWeek` is the week that lineups, standings and free agency key on.
// They are the same integer for every scoring week — the one place they
// diverge is CFB week 0, the late-August opening weekend, which plays and
// displays but awards no fantasy points and therefore has no lineup week.
// `resolveWeek()` in backend/schedule.js is what splits it out of CFBD week 1.
//
// `dates` are Tuesday-Monday windows around each week's slate, matching the
// lineup lock boundary rather than the gameday range. They are hardcoded
// because roughly half of kickoff times are TBD until ~12 days out, so
// deriving them from `games` would show a calendar that shifts under the user.
// Re-derive them each August with a query grouping `games` by week.
export const SEASON_WEEKS = [
  { cfbWeek: 0,  lineupWeek: null, dates: "Aug 25–31",    scores: false },
  { cfbWeek: 1,  lineupWeek: 1,    dates: "Sep 1–7",      scores: true },
  { cfbWeek: 2,  lineupWeek: 2,    dates: "Sep 8–14",     scores: true },
  { cfbWeek: 3,  lineupWeek: 3,    dates: "Sep 15–21",    scores: true },
  { cfbWeek: 4,  lineupWeek: 4,    dates: "Sep 22–28",    scores: true },
  { cfbWeek: 5,  lineupWeek: 5,    dates: "Sep 29–Oct 5", scores: true },
  { cfbWeek: 6,  lineupWeek: 6,    dates: "Oct 6–12",     scores: true },
  { cfbWeek: 7,  lineupWeek: 7,    dates: "Oct 13–19",    scores: true },
  { cfbWeek: 8,  lineupWeek: 8,    dates: "Oct 20–26",    scores: true },
  { cfbWeek: 9,  lineupWeek: 9,    dates: "Oct 27–Nov 2", scores: true },
  { cfbWeek: 10, lineupWeek: 10,   dates: "Nov 3–9",      scores: true },
  { cfbWeek: 11, lineupWeek: 11,   dates: "Nov 10–16",    scores: true },
  { cfbWeek: 12, lineupWeek: 12,   dates: "Nov 17–23",    scores: true },
  { cfbWeek: 13, lineupWeek: 13,   dates: "Nov 24–30",    scores: true },
];

/** The 13 scoring weeks, in order. */
export const LINEUP_WEEKS = SEASON_WEEKS.filter((w) => w.scores);

// Phases are carried by row colour rather than a per-row label, so the legend
// and the rows have to read from one definition. `dot` is the legend swatch,
// `row` is the row tint — kept faint so 14 tinted rows still look like a table.
export const PHASES = {
  preseason: { key: "preseason", label: "No scoring", dot: "bg-hairline", row: "bg-surface-subtle/70" },
  regular: { key: "regular", label: "Regular Season", dot: "bg-success", row: "bg-success-bg/60" },
  playoffs: { key: "playoffs", label: "Playoffs", dot: "bg-brand", row: "bg-brand-bg/60" },
};

export const PHASE_LEGEND = [PHASES.preseason, PHASES.regular, PHASES.playoffs];

/**
 * Which phase a week belongs to. Pass the lineup week, or null for CFB week 0.
 * Playoff rounds are not split out — which round the opening playoff week is
 * depends on league size, and the rounds share one colour anyway.
 */
export function getWeekPhase(lineupWeek) {
  if (lineupWeek === null) return PHASES.preseason;
  if (lineupWeek <= REGULAR_SEASON_WEEKS) return PHASES.regular;
  return PHASES.playoffs;
}

// config.season.currentWeek holds one of these three interchangeable
// non-numeric labels before the admin sets a real lineup week (backend/
// cfbd.js and backend/espn.js check the same list, copied rather than
// imported since the backend cannot import frontend utils). A literal
// `=== "Preseason"` check missed "Off-Season"/"Offseason" and silently
// never fired.
const OFF_SEASON_LABELS = ["Preseason", "Off-Season", "Offseason"];
export function isOffSeason(currentWeek) {
  return !currentWeek || OFF_SEASON_LABELS.includes(currentWeek);
}

/**
 * The lineup week to render for a config.season.currentWeek value.
 *
 * Always a number and never below 1. CFB week 0 is the opening weekend: it
 * plays and counts toward a team's record, but it awards no fantasy points and
 * has no lineup, so rosters compile into week 1 and there is nothing to show
 * under week 0.
 *
 * The floor is not cosmetic. Consumers guard on truthiness
 * (`if (!currentWeek) return`) and build keys as `week${currentWeek}`, so a
 * literal 0 makes effects silently never run and lookups silently miss. Setting
 * the opening weekend live took out the Scoreboard, Teams and Lineup tabs with
 * nothing in the console.
 *
 * The database floors identically, with GREATEST(v_raw_week::INT, 1) in
 * compile_draft_rosters and instant_add_team.
 *
 * Callers that must tell week 0 apart from week 1 should read
 * seasonConfig.currentWeek directly rather than use this.
 */
export function resolveLineupWeek(currentWeek) {
  const parsed = typeof currentWeek === "number"
    ? currentWeek
    : parseInt(String(currentWeek ?? "").match(/\d+/)?.[0] ?? "", 10);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
}
